/**
 * Máy chủ giả của bán token Fox (ADR-0100, tenant-service): `/api/v1/security/top-up-packs`,
 * `/api/v1/tenants/{id}/top-up-packs`, `/api/v1/security/ai-token-pricing`, `/api/v1/tenants/{id}/ai-token-quota/models`.
 *
 * Tính như backend (`FoxPricing`): giá gói = giá ghi đè, không có thì token / 1e6 × đơn giá làm tròn cent; VND theo tỷ
 * giá USD→VND đang hiệu lực ở Tỷ giá của Bảng giá model (`mock/gia-ai.ts`), làm tròn đồng. Phép tính dùng chung
 * `modules/goi-nap-token/nghiep-vu/fox.ts`. Màu và nhãn không ở đây.
 *
 * Tình huống mẫu: ba gói đang bán (một gói có giá ghi đè), một gói đã ngừng; đơn giá 2,5 USD cho 1 triệu token Fox.
 */
import type { ApiError, LoiTruong } from '../lib/errors';
import { sinhMa } from '../lib/ma-tu-sinh';
import { giaGoiUsd, giaVnd, kiemDonGia, MAU_USD, thoiHanHopLe } from '../modules/goi-nap-token/nghiep-vu/fox';
import type {
  AiTokenModelUsage,
  AiTokenPricing,
  AiTokenPricingUpdate,
  ExchangeRateSnapshot,
  TopUpCatalog,
  TopUpPack,
  TopUpPackCreate,
  TopUpPackUpdate,
} from '../modules/goi-nap-token/types';
import { useAuthStore } from '../stores/auth';
import { mayChuModelAiNenTang } from './ai-model';
import { mayChuGiaAi } from './gia-ai';

type GoiLuu = Omit<TopUpPack, 'effective_price_usd' | 'effective_price_vnd'>;

const LUC = '2026-10-27T09:00:00+07:00';

let cacGoi: GoiLuu[] = [
  {
    code: 'NAP_5_TRIEU',
    name: 'Nạp 5 triệu token',
    fox_tokens: 5_000_000,
    validity_unit: 'MONTH',
    validity_count: 1,
    price_usd: null,
    is_active: true,
    created_at: LUC,
    updated_at: LUC,
  },
  {
    code: 'NAP_20_TRIEU',
    name: 'Nạp 20 triệu token',
    fox_tokens: 20_000_000,
    validity_unit: 'MONTH',
    validity_count: 3,
    price_usd: '45.00',
    is_active: true,
    created_at: LUC,
    updated_at: LUC,
  },
  {
    code: 'CAO_DIEM_2_TUAN',
    name: 'Cao điểm 2 tuần',
    fox_tokens: 8_000_000,
    validity_unit: 'WEEK',
    validity_count: 2,
    price_usd: null,
    is_active: true,
    created_at: LUC,
    updated_at: LUC,
  },
  {
    code: 'NAP_100_TRIEU',
    name: 'Nạp 100 triệu token',
    fox_tokens: 100_000_000,
    validity_unit: 'MONTH',
    validity_count: 12,
    price_usd: '220.00',
    is_active: false,
    created_at: '2026-09-15T09:00:00+07:00',
    updated_at: '2026-10-20T10:00:00+07:00',
  },
];

let giaBan = {
  don_gia_usd_moi_trieu_token_fox: '2.5' as string | null,
  muc_nap_usd: [10, 50, 100, 500],
  updated_by: null as string | null,
  updated_at: LUC,
};

function loiTruong(pointer: string, detail: string): ApiError {
  return {
    status: 400,
    code: 'INVALID_ARGUMENT',
    message: 'Thông tin chưa đúng, vui lòng sửa trường được đánh dấu.',
    details: [{ pointer, detail }],
  };
}

/** Tỷ giá USD→VND đang hiệu lực hôm nay ở Tỷ giá của Bảng giá model; `null` khi chưa có. */
export function tyGiaUsdVnd(): ExchangeRateSnapshot | null {
  const homNay = new Date().toISOString().slice(0, 10);
  const dong = mayChuGiaAi
    .danhSachTyGia()
    .items.filter(
      (d) =>
        d.from_currency === 'USD' &&
        d.to_currency === 'VND' &&
        d.effective_from <= homNay &&
        (d.effective_to === null || d.effective_to > homNay),
    )
    .sort((a, b) => b.effective_from.localeCompare(a.effective_from) || b.id - a.id)[0];
  return dong
    ? { rate: dong.rate, rate_id: dong.id, effective_from: dong.effective_from, source: dong.source }
    : null;
}

function coGia(g: GoiLuu): TopUpPack {
  const usd = giaGoiUsd(g.fox_tokens, g.price_usd, giaBan.don_gia_usd_moi_trieu_token_fox);
  return { ...g, effective_price_usd: usd, effective_price_vnd: giaVnd(usd, tyGiaUsdVnd()?.rate) };
}

const theoThuTu = (a: GoiLuu, b: GoiLuu) =>
  Number(b.is_active) - Number(a.is_active) || a.fox_tokens - b.fox_tokens || a.name.localeCompare(b.name, 'vi');

function kiem(du: TopUpPackCreate | TopUpPackUpdate, taoMoi: boolean): LoiTruong[] {
  const loi: LoiTruong[] = [];
  if (taoMoi || du.name !== undefined) {
    const ten = du.name?.trim() ?? '';
    if (!ten) loi.push({ pointer: '#/name', detail: 'Nhập tên gói.' });
  }
  if ((taoMoi || du.fox_tokens !== undefined) && !(Number.isInteger(du.fox_tokens) && (du.fox_tokens ?? 0) >= 1)) {
    loi.push({ pointer: '#/fox_tokens', detail: 'Số token Fox của gói phải từ 1 tới 1.000.000.000.000.' });
  }
  if (du.price_usd !== undefined && du.price_usd !== null && (!MAU_USD.test(du.price_usd) || Number(du.price_usd) <= 0)) {
    loi.push({ pointer: '#/price_usd', detail: 'Giá USD phải lớn hơn 0, tối đa 2 chữ số thập phân.' });
  }
  return loi;
}

/** Số theo model của một tenant: tỉ lệ cố định để màn có số đủ loại (model hệ số > 1, < 1, lời ghi không model). */
const TI_LE: Array<[string | null, number]> = [
  ['gemini', 0.34],
  ['claude', 0.12],
  ['foxai', 0.41],
  ['qwen3.8-27b', 0.1],
  [null, 0.03],
];

export const mayChuGoiNapToken = {
  danhSach(): TopUpPack[] {
    return [...cacGoi].sort(theoThuTu).map(coGia);
  },

  tao(du: TopUpPackCreate): TopUpPack {
    const loi = kiem(du, true);
    if (!thoiHanHopLe(du.validity_unit, du.validity_count)) {
      loi.push({ pointer: '#/validity_count', detail: 'Thời hạn gói nạp token là 1, 3, 6, 12 tháng hoặc 1 tới 4 tuần.' });
    }
    if (loi.length) throw { ...loiTruong('', ''), details: loi };
    const goc = du.code?.trim() || sinhMa(du.name, { tienTo: 'GOI', toiDa: 40 });
    let code = goc;
    for (let i = 2; cacGoi.some((g) => g.code === code); i += 1) code = `${goc}_${i}`;
    const bayGio = new Date().toISOString();
    const moi: GoiLuu = {
      code,
      name: du.name.trim(),
      fox_tokens: du.fox_tokens,
      validity_unit: du.validity_unit,
      validity_count: du.validity_count,
      price_usd: du.price_usd ? Number(du.price_usd).toFixed(2) : null,
      is_active: du.is_active ?? true,
      created_at: bayGio,
      updated_at: bayGio,
    };
    cacGoi = [...cacGoi, moi];
    return coGia(moi);
  },

  sua(code: string, du: TopUpPackUpdate): TopUpPack {
    const cu = cacGoi.find((g) => g.code === code);
    if (!cu) throw { status: 404, code: 'NOT_FOUND', message: 'Không tìm thấy gói nạp token này.' } as ApiError;
    const loi = kiem(du, false);
    const donVi = du.validity_unit ?? cu.validity_unit;
    const so = du.validity_count ?? cu.validity_count;
    if (!thoiHanHopLe(donVi, so)) {
      loi.push({ pointer: '#/validity_count', detail: 'Thời hạn gói nạp token là 1, 3, 6, 12 tháng hoặc 1 tới 4 tuần.' });
    }
    if (loi.length) throw { ...loiTruong('', ''), details: loi };
    const moi: GoiLuu = {
      ...cu,
      ...(du.name !== undefined ? { name: du.name.trim() } : {}),
      ...(du.fox_tokens !== undefined ? { fox_tokens: du.fox_tokens } : {}),
      validity_unit: donVi,
      validity_count: so,
      ...(du.price_usd !== undefined ? { price_usd: du.price_usd === null ? null : Number(du.price_usd).toFixed(2) } : {}),
      ...(du.is_active !== undefined ? { is_active: du.is_active } : {}),
      updated_at: new Date().toISOString(),
    };
    cacGoi = cacGoi.map((g) => (g.code === code ? moi : g));
    return coGia(moi);
  },

  /** Một gói theo mã (kể cả đã ngừng), cho yêu cầu nạp token. */
  tim(code: string): TopUpPack | undefined {
    const g = cacGoi.find((x) => x.code === code);
    return g ? coGia(g) : undefined;
  },

  catalog(): TopUpCatalog {
    return {
      items: this.danhSach()
        .filter((g) => g.is_active)
        .map((g) => ({
          code: g.code,
          name: g.name,
          fox_tokens: g.fox_tokens,
          validity_unit: g.validity_unit,
          validity_count: g.validity_count,
          price_usd: g.effective_price_usd,
          price_vnd: g.effective_price_vnd,
        })),
      don_gia_usd_moi_trieu_token_fox: giaBan.don_gia_usd_moi_trieu_token_fox,
      muc_nap_usd: [...giaBan.muc_nap_usd],
      exchange_rate: tyGiaUsdVnd(),
    };
  },

  giaBan(): AiTokenPricing {
    return { ...giaBan, muc_nap_usd: [...giaBan.muc_nap_usd], exchange_rate: tyGiaUsdVnd() };
  },

  datGiaBan(du: AiTokenPricingUpdate): AiTokenPricing {
    const dg = du.don_gia_usd_moi_trieu_token_fox === null ? { giaTri: null, loi: null } : kiemDonGia(du.don_gia_usd_moi_trieu_token_fox);
    if (dg.loi) {
      throw loiTruong(
        '#/don_gia_usd_moi_trieu_token_fox',
        'Đơn giá USD cho 1 triệu token Fox phải lớn hơn 0, tối đa 6 chữ số thập phân, ví dụ 2.5.',
      );
    }
    giaBan = {
      don_gia_usd_moi_trieu_token_fox: dg.giaTri,
      muc_nap_usd: [...du.muc_nap_usd],
      updated_by: useAuthStore.getState().user?.id ?? null,
      updated_at: new Date().toISOString(),
    };
    return this.giaBan();
  },

  /** Đơn giá hiện hành (cho yêu cầu nạp theo số tiền). */
  donGia(): string | null {
    return giaBan.don_gia_usd_moi_trieu_token_fox;
  },

  theoModel(tenantId: string, thang: string | undefined, tongFox: number): AiTokenModelUsage {
    const m = thang ?? new Date().toISOString().slice(0, 7);
    const models = mayChuModelAiNenTang.danhSach().items;
    const items = TI_LE.map(([key, ti]) => {
      const model = key ? models.find((x) => x.key === key) : undefined;
      const heSo = model?.he_so_token ?? 1;
      const fox = Math.round(tongFox * ti);
      const raw = Math.floor(fox / heSo);
      return {
        model_key: key,
        label: model?.label ?? null,
        raw_tokens: raw,
        fox_tokens: fox,
        calls: Math.max(1, Math.round(raw / 9_000)),
        he_so_token: heSo,
      };
    })
      .filter((d) => d.fox_tokens > 0)
      .sort((a, b) => b.fox_tokens - a.fox_tokens);
    return {
      tenant_id: tenantId,
      month: `${m}-01`,
      raw_tokens: items.reduce((s, d) => s + d.raw_tokens, 0),
      fox_tokens: items.reduce((s, d) => s + d.fox_tokens, 0),
      items,
    };
  },
};
